import React, { useContext, useEffect } from "react";
import { Route, Outlet, RouterProvider, createBrowserRouter, createRoutesFromElements, useLocation, useNavigate } from "react-router-dom";
import {
  SkipNav,
  ToastOutlet,
  USGovernmentBanner
} from "@boozallen-uip/uswds-react-core";

import packageJson from "../package.json";
import HyperlinkListContainer from "./pages/Hyperlink/HyperlinkListContainer";
import ProductDetailListContainerFC from "./pages/Product/ProductDetailListContainerFC";
import ResourceSection from "./pages/ResourceSection";
import Callback from "./pages/Callback/Callback";
import ProductListContainerFC from "./pages/Product/ProductListContainerFC";
import { UserContext } from "./pages/UserContext";
import VendorDashboard from "./pages/VendorDashboard/VendorDashboard.page";
import VendorOverview from "./pages/Vendor/VendorOverview.page";
import isTokenValid from "./utlities/auth.utility";
import ProductListUnauthorized from "./pages/Product/ProductList/ProductList.unauth.page";
import Footer from "./components/Footer.component";
import AuthGuard from "./components/AuthGuard.component"
import ProductSubmit from "./pages/Product/ProductSubmit.page";
import FirstTimeLogin from "./pages/Account/FirstTimeLogin.page";
import AppShell from "./AppShell";
import ForVendors from "./pages/Vendor/ForVendors.page";
import UserRequestApprover from "./pages/Account/UserRequestApprover.page";
import Welcome from "./pages/Home/Welcome";
import Resources from "./pages/Resources";
import TermsOfUse from "./pages/TermsOfUse.page";
import VendorEdit from "./pages/Vendor/VendorEdit.page";
import LandingPage from "./pages/Home/LandingPage";
import ContactPage from "./pages/Contact/Contact.page";
import AnalyticsPage from "./pages/Analytics/Analytics.page";
import * as Constants from "./utlities/core.constants";
import AwaitUserCheck from "./components/AwaitUserCheck.component";
import Vendor from "./pages/Vendor/Vendor.page";
import HeaderWithLogin from "./pages/HeaderWithLogin";
// import LandingHeader from "./pages/LandingHeader";
import OrganizationCreate from "./pages/Organization/OrganizationCreate.page";
import SuperUserTools from "./pages/SuperUser/SuperUserTools.page";
import { NotificationsPage } from "./pages/Notification";
import { EditorRoutes, ProtectedEditorRoute } from "./components/acr-editor/routes";
import { EditorIndex, Overview, About, Chapter, Report, Glossary } from "./components/acr-editor/pages";
import { LoginModalProvider } from "./components/modals/LoginModalContext";
import AppModal from "./components/modals/AppModal";
import PersistentDisclaimer from "./components/disclaimer/PersistentDisclaimer";
import ImpersonationBanner from "./components/ImpersonationBanner.component";
import { InactivityWarningContext, InactivityWarningProvider } from "./contexts/InactivityWarningContext";
import useSessionExpiry from "./hooks/useSessionExpiry.hook";
import { INACTIVITY_WARNING_DURATION_MS, HOME_URL } from "./utlities/core.constants";
import { registerRouter } from "./utlities/routerRegistry";

function getWarningDurationLabel() {
  if (INACTIVITY_WARNING_DURATION_MS % 60000 === 0) {
    const minutes = INACTIVITY_WARNING_DURATION_MS / 60000;
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const seconds = Math.max(1, Math.round(INACTIVITY_WARNING_DURATION_MS / 1000));
  return `${seconds} second${seconds === 1 ? "" : "s"}`;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function RouterFrame() {
  const bannerWrapperRef = React.useRef(null);

  useEffect(() => {
    const decorativeIcons = bannerWrapperRef.current?.querySelectorAll(".usa-banner__guidance .usa-banner__icon");
    if (!decorativeIcons?.length) {
      return;
    }
    decorativeIcons.forEach((icon) => {
      icon.setAttribute("alt", "");
      icon.setAttribute("aria-hidden", "true");
      icon.setAttribute("role", "presentation");
    });
  }, []);

  return (
    <LoginModalProvider>
      <InactivityWarningProvider>
      <ScrollToTop />
      <SkipNav href="#main-content" />
      <GlobalAuthErrorModal />
      <InactivityWarningModal />
      <SessionExpiryManager />

      <div id="headerAndMain">
        <div ref={bannerWrapperRef}>
          <USGovernmentBanner className="bg-blue-500" />
        </div>
        <HeaderWithLogin /> {/* <LandingHeader /> is currently unused, kept in case the header design reverts. */}

        <main id="main-content">
          <Outlet />
        </main>
      </div>

      <ShortDisclaimer />
      <ResourceSection />
      <Footer />
      <ToastOutlet />
      <ImpersonationBanner />
      </InactivityWarningProvider>
    </LoginModalProvider>
  );
}

const SHORT_DISCLAIMER_STORAGE_KEY = "acr_short_disclaimer_collapsed";

function isShortDisclaimerPage(pathname) {
  if (!pathname) return false;

  return (
    pathname === Constants.FOR_BUYERS_URL
    || pathname === Constants.PRODUCT_BASE_URL
    || pathname.startsWith(`${Constants.PRODUCT_BASE_URL}/`)
    || pathname.startsWith("/product-details/")
    || pathname === Constants.FOR_VENDORS_URL
    || pathname === Constants.VENDOR_BASE_URL
    || pathname.startsWith(`${Constants.VENDOR_BASE_URL}/`)
    || pathname.startsWith(Constants.ACR_EDITOR_BASE_URL)
  );
}

function ShortDisclaimer() {
  const { pathname } = useLocation();
  const shouldShow = isShortDisclaimerPage(pathname);

  if (!shouldShow) return null;

  return (
    <section className="mx-auto w-full max-w-screen-xl pb-16 text-left">
      <PersistentDisclaimer
        storageKey={SHORT_DISCLAIMER_STORAGE_KEY}
        title="Disclaimer"
      >
        <p className="m-0">
          The information on this site is provided &quot;as is&quot; for general informational purposes only. GSA does not warrant the completeness, accuracy, or currency of any content, including vendor-submitted Accessibility Conformance Reports (ACRs), which reflect conditions at the time of creation. Users should independently verify information before making procurement or compliance decisions. GSA assumes no liability for decisions made based on this content. Reference to any product or vendor does not constitute federal endorsement.
        </p>
      </PersistentDisclaimer>
    </section>
  );
}

function GlobalAuthErrorModal() {
  const { authError, setAuthError, setJwt } = useContext(UserContext);
  const navigate = useNavigate();

  const signOut = () => {
    setAuthError(null);
    setJwt(null);
    navigate(HOME_URL, { replace: true });
  };

  const handleAuthBlockModalStateChanged = (nextState) => {
    if (typeof nextState === "boolean" && !nextState) {
      setAuthError(null);
    }
  };

  return (
    <AppModal
      id="inactive-account-modal"
      isOpen={Boolean(authError)}
      onStateChanged={handleAuthBlockModalStateChanged}
      title="Login blocked"
      body={authError || ""}
      primaryActionText="Close/Acknowledge"
      onPrimaryAction={signOut}
      forceAction
    />
  );
}

function InactivityWarningModal() {
  const { isWarningOpen, continueSession } = useContext(InactivityWarningContext);
  const warningDurationLabel = getWarningDurationLabel();
  const handleWarningModalStateChanged = () => {};

  return (
    <AppModal
      id="inactivity-warning-modal"
      isOpen={isWarningOpen}
      onStateChanged={handleWarningModalStateChanged}
      title="Inactivity warning"
      body={`You will be logged out in ${warningDurationLabel} due to inactivity. If you wish to remain logged in, please select Continue.`}
      primaryActionText="Continue"
      onPrimaryAction={continueSession}
      forceAction
    />
  );
}

// Headless: silently re-authenticates an active user before their Login.gov token expires.
// Renders nothing (no prompt) — see useSessionExpiry for the rationale.
function SessionExpiryManager() {
  useSessionExpiry();
  return null;
}

function AuthenticatedProductListRoute() {
  const { token } = useContext(UserContext);
  const isAuthenticated = isTokenValid(token);

  return isAuthenticated ? <ProductListContainerFC /> : <ProductListUnauthorized />;
}

function BuyerProductListRoute() {
  return (
    <AwaitUserCheck>
      <AuthenticatedProductListRoute />
    </AwaitUserCheck>
  );
}

const basename = packageJson.homepage ? packageJson.homepage : undefined;
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<RouterFrame />}>
      <Route path="" element={<AppShell />}>
        <Route path="" element={<LandingPage />} />
        <Route
            path={Constants.HYPERLINK_LIST_URL}
            loader={async () => {
              document.title = "My new title";
              return null;
            }}
            element={<HyperlinkListContainer />}
          />
        <Route path={Constants.PRODUCT_DETAIL_URL} element={<AwaitUserCheck><AuthGuard><ProductDetailListContainerFC /></AuthGuard></AwaitUserCheck>} />
        <Route path={Constants.PRODUCT_LIST_SUBMIT_URL} element={<AuthenticatedProductListRoute />} />
        <Route path={Constants.FOR_BUYERS_URL} element={<BuyerProductListRoute />} />
        <Route path={Constants.PRODUCT_BASE_URL} element={<AwaitUserCheck><Outlet /></AwaitUserCheck>}>
          <Route path="submit" element={<ProductSubmit />} />
          <Route path="edit/:productId" element={<ProductSubmit />} />
        </Route>
        <Route path={Constants.CALLBACK_URL} element={<Callback />} />
        <Route path={Constants.CONTACT_URL} element={<ContactPage />} />
        <Route path={Constants.VENDOR_BASE_URL} element={<AwaitUserCheck><Outlet /></AwaitUserCheck>}>
          <Route path="dashboard" element={<VendorDashboard />} />
          <Route path="overview" element={<VendorOverview />} />
          <Route path=":vendorId" element={<Vendor />} />
          <Route path="edit" element={<VendorEdit />} />
        </Route>
        <Route path="/organization" element={<AwaitUserCheck><Outlet /></AwaitUserCheck>}>
          <Route path="create" element={<OrganizationCreate />} />
        </Route>
        <Route path={Constants.ENTITY_APPROVAL_URL} element={<AwaitUserCheck><UserRequestApprover /></AwaitUserCheck>} />
        <Route path={Constants.ACCOUNT_FIRST_TIME_URL} element={<FirstTimeLogin />} />
        <Route path={Constants.FOR_VENDORS_URL} element={<ForVendors />} />
        <Route path={Constants.WELCOME_URL} element={<Welcome />} />
        <Route path={Constants.RESOURCES_URL} element={<Resources />} />
        <Route path={Constants.NOTIFICATIONS_URL} element={<AwaitUserCheck><NotificationsPage /></AwaitUserCheck>} />
        <Route path={Constants.ANALYTICS_URL} element={<AwaitUserCheck><AnalyticsPage /></AwaitUserCheck>} />
        <Route path={Constants.SUPERUSER_TOOLS_URL} element={<AwaitUserCheck><SuperUserTools /></AwaitUserCheck>} />
        <Route path={Constants.ACR_EDITOR_BASE_URL} element={<ProtectedEditorRoute><EditorRoutes /></ProtectedEditorRoute>}>
          <Route index element={<EditorIndex />} />
          <Route path="overview" element={<Overview />} />
          <Route path="about" element={<About />} />
          <Route path="chapter/:chapterId" element={<Chapter />} />
          <Route path="report" element={<Report />} />
          <Route path="glossary" element={<Glossary />} />
        </Route>
        <Route path="*" element={<LandingPage />} />
      </Route>
      {/* Outside AppShell so the first-time/inactive-account redirect doesn't gate it —
          first-time users must be able to open the Terms of Use link from account creation. */}
      <Route path={Constants.TERMS_OF_USE_URL} element={<TermsOfUse />} />
    </Route>
  ),
  { basename }
);

registerRouter(router);

export default function RouterShell() {
  return (
    <RouterProvider router={router} />
  );
}
